import { useEffect } from "react";
import { useReadFile } from "../../hooks";

type FilePreviewProps = {
  url: string;
  fileName?: string;
};

const getFileExtension = (url: string): string => {
  return url.split(".").pop()?.toLowerCase() || "";
};

const FilePreview: React.FC<FilePreviewProps> = ({ url, fileName }) => {
  const extension = getFileExtension(url);

  const {
    data: urlRead,
    mutate: readFile,
    isPending,
    isError,
    error,
  } = useReadFile(true);

  useEffect(() => {
    readFile(url);
  }, [url, readFile]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        Loading preview...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <p>{error?.message}</p>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-blue-500 hover:underline"
            download
          >
            Download {fileName || "file"}
          </a>
        )}
      </div>
    );
  }

  const isGoogleDocsSupported = [
    "pdf",
    "doc",
    "docx",
    "ppt",
    "pptx",
    "xls",
    "xlsx",
    "txt",
    "rtf",
  ].includes(extension);
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
    extension
  );
  const isVideo = ["mp4", "webm", "ogg"].includes(extension);

  if (isGoogleDocsSupported && urlRead) {
    const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(urlRead)}&embedded=true`;
    return (
      <iframe
        src={googleViewerUrl}
        title={fileName || "Document Preview"}
        width="100%"
        height="600px"
        style={{ border: "1px solid #ccc", borderRadius: "8px" }}
      />
    );
  }

  if (isImage) {
    return (
      <img
        src={url}
        alt={fileName || "Preview"}
        style={{ maxWidth: "100%", maxHeight: 500 }}
      />
    );
  }

  if (isVideo) {
    return (
      <video controls style={{ maxWidth: "100%", maxHeight: 500 }}>
        <source src={url} type={`video/${extension}`} />
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-64">
      <p>Cannot preview this file type.</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 text-blue-500 hover:underline"
        download
      >
        Download {fileName || "file"}
      </a>
    </div>
  );
};

export default FilePreview;
